import React from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import activities from '../../../../api/activities/hooks'
import { PermissionsProps } from '../../../../api/globalTypes'
import { MODAL_TYPES } from '../../../../common/constants'
import { showModal } from '../../../../store/modal'
import { formatDateTime } from '../../../../utils/general'
import AppPermissionsSwitcher from '../../../AppPermissionsSwitcher/AppPermissionsSwitcher'
import { action } from '../../../AppPermissionsSwitcher/constants/actions'
import { DeleteButton } from '../../../Common/Buttons'
import { setDeleteCallback } from '../../DeleteModal/utils'
import { ActivityModalData } from '../types'

interface InfoRowProps {
    icon: string
    children: React.ReactNode
    secondary?: boolean
}

const InfoRow = ({ icon, children, secondary = false }: InfoRowProps) => (
    <div className="d-flex mb-2">
        <div className="flex-grow-1 d-flex align-items-center">
            <div className="flex-shrink-0 me-3">
                <i className={`mdi ${icon} text-secondary fs-16`}></i>
            </div>
            <div className="flex-grow-1">
                <span className={`d-block fw-light mb-0${secondary ? ' text-secondary' : ''}`} id="event-start-date-tag">
                    {children}
                </span>
            </div>
        </div>
    </div>
)

interface InfoContentProps {
    event: ActivityModalData
    onEdit: () => void
    onClose: () => void
}

const InfoContent = ({ event, onEdit, onClose }: InfoContentProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    /** TODO: agents filter in ActivityCalendar is currently controlled by role (ROLE_ADMIN), so this permissions are probably obsolete 
     - need to check if agents fliter should be allowed for ROLE_USER */
    // data needed for permissions
    // contactPersonCompany - company that was added to contact
    // contactCompany - standalone company
    const activityAgentId = event?.activityAgent?.[0]?.agent?.id
    const contactAgentId = event?.contact?.agent?.id
    const contactAgencyOfficeId = event?.contact?.agent?.agencyOffice?.id
    const companyAgentId = event?.contact?.contactPersonCompany?.[0]?.company?.agent?.id ?? event?.contactCompany?.[0]?.agent?.id
    const companyAgencyOfficeId =
        event?.contact?.contactPersonCompany?.[0]?.company?.agent?.agencyOffice?.id ?? event?.contactCompany?.[0]?.agent?.agencyOffice?.id
    const propertyAgentId = event?.property?.[0]?.agent?.id
    const propertyAgencyOfficeId = event?.property?.[0]?.agent?.agencyOffice?.id
    const projectAgentId = event?.project?.[0]?.agent?.id
    const projectAgencyOfficeId = event?.project?.[0]?.agent?.agencyOffice?.id

    const showContactData = event?.contact?.contactPerson || event?.contactCompany?.length > 0

    const { mutate: deleteActivity } = activities.DeleteActivity.useMutation()

    const handleDelete = () => {
        const callbackId = 'delete-activity'
        const payload = {
            modalType: MODAL_TYPES.DELETE_MODAL,
            modalProps: { callbackId },
        }

        setDeleteCallback(callbackId, () => {
            deleteActivity(Number(event.id))
            onClose()
        })

        dispatch(showModal(payload))
    }

    return (
        <>
            <ModalHeader
                toggle={onClose}
                tag="h5"
                className="p-3 modal-title"
                style={{ background: event?.backgroundColor ? event.backgroundColor : '' }}
            >
                {event.title}
            </ModalHeader>
            <ModalBody>
                <div>
                    <div className="text-end">
                        <AppPermissionsSwitcher
                            permission="ModulePermissions"
                            activityAgentId={activityAgentId}
                            module={'activities'}
                            action={action.edit}
                        >
                            {({ hasModulePermission }: PermissionsProps) =>
                                hasModulePermission && (
                                    <Button className="btn btn-sm btn-edit" id="edit-event-btn" onClick={onEdit}>
                                        {t('button.edit')}
                                    </Button>
                                )
                            }
                        </AppPermissionsSwitcher>
                    </div>

                    {showContactData && (
                        <InfoRow icon="mdi-calendar-account-outline">
                            <AppPermissionsSwitcher
                                permission="ModulePermissions"
                                itemAgentId={contactAgentId}
                                itemAgentOfficeId={contactAgencyOfficeId}
                                module={'contacts'}
                                action={action.view}
                            >
                                {({ hasModulePermission }: PermissionsProps) =>
                                    event?.contact?.contactPerson && (
                                        <>
                                            <Link
                                                to={hasModulePermission ? `/contacts/${event.contact.id}` : '#'}
                                                target={hasModulePermission ? '_blank' : undefined}
                                                className={hasModulePermission ? 'dmn-link' : undefined}
                                                style={{ cursor: hasModulePermission ? 'pointer' : 'auto' }}
                                            >
                                                {event.contact.contactPerson.firstName} {event.contact.contactPerson.lastName}
                                            </Link>
                                            {event?.contactCompany.length > 0 && ', '}
                                        </>
                                    )
                                }
                            </AppPermissionsSwitcher>

                            <AppPermissionsSwitcher
                                permission="ModulePermissions"
                                itemAgentId={companyAgentId}
                                itemAgentOfficeId={companyAgencyOfficeId}
                                module={'companies'}
                                action={action.view}
                            >
                                {({ hasModulePermission }: PermissionsProps) =>
                                    event?.contactCompany?.map((item, index, array) => (
                                        <React.Fragment key={item.id}>
                                            <Link
                                                to={hasModulePermission ? `/companies/${item.id}` : '#'}
                                                target={hasModulePermission ? '_blank' : undefined}
                                                className={hasModulePermission ? 'dmn-link' : undefined}
                                                style={{ cursor: hasModulePermission ? 'pointer' : 'auto' }}
                                            >
                                                {item.contactPersonCompany?.[0]?.company?.contactCompany?.name ?? item.contactCompany?.name}
                                            </Link>
                                            {index < array.length - 1 && ', '}
                                        </React.Fragment>
                                    )) ?? ''
                                }
                            </AppPermissionsSwitcher>
                        </InfoRow>
                    )}

                    {event?.activityType && (
                        <InfoRow icon="mdi-calendar-text-outline">
                            {event.activityType.name},{' '}
                            {event.completed ? (
                                <span className="text-success">{t('modal.activity.field.completed')}</span>
                            ) : (
                                t('modal.activity.field.planned')
                            )}
                        </InfoRow>
                    )}
                    {(event?.dateFrom || event?.dateTo) && (
                        <InfoRow icon="mdi-calendar">
                            {`${t('app.common.from').toLowerCase()} ${formatDateTime(event.dateFrom)} `}
                            {`${t('app.common.to').toLowerCase()} ${formatDateTime(event.dateTo)}`}
                        </InfoRow>
                    )}
                    {(event?.timeFrom || event?.timeTo) && (
                        <InfoRow icon="mdi-clock-outline">
                            {event && event.timeFrom ? `${t('app.common.from').toLowerCase()} ${event.timeFrom} ` : ''}
                            {event && event.timeTo ? `${t('app.common.to').toLowerCase()} ${event.timeTo}` : ''}
                        </InfoRow>
                    )}
                    <AppPermissionsSwitcher
                        permission="ModulePermissions"
                        itemAgentId={propertyAgentId}
                        itemAgentOfficeId={propertyAgencyOfficeId}
                        module={'properties'}
                        action={action.view}
                    >
                        {({ hasModulePermission }: PermissionsProps) =>
                            hasModulePermission &&
                            event?.property?.length > 0 && (
                                <>
                                    <InfoRow icon="mdi-domain">
                                        {event.property.map((item, index, array) => (
                                            <React.Fragment key={item.id}>
                                                <Link to={`/properties/${item.id}`} target="_blank" className="dmn-link">
                                                    ID: {item.internalAppId} - {item.propertyType}
                                                </Link>
                                                {index < array.length - 1 && ', '}
                                            </React.Fragment>
                                        ))}
                                    </InfoRow>
                                    {event?.location && <InfoRow icon="mdi-map-marker-outline">{event.location}</InfoRow>}
                                </>
                            )
                        }
                    </AppPermissionsSwitcher>
                    <AppPermissionsSwitcher
                        permission="ModulePermissions"
                        itemAgentId={projectAgentId}
                        itemAgentOfficeId={projectAgencyOfficeId}
                        module={'projects'}
                        action={action.view}
                    >
                        {({ hasModulePermission }: PermissionsProps) =>
                            hasModulePermission &&
                            event?.project?.length > 0 && (
                                <>
                                    <InfoRow icon="mdi-layers-outline">
                                        {event.project.map((item, index, array) => (
                                            <React.Fragment key={item.id}>
                                                <Link to={`/projects/${item.id}`} target="_blank" className="dmn-link">
                                                    ID: {item.internalAppId} - {item.projectTitle}
                                                </Link>
                                                {index < array.length - 1 && ', '}
                                            </React.Fragment>
                                        ))}
                                    </InfoRow>
                                    {!event?.property?.length && event?.location && <InfoRow icon="mdi-map-marker-outline">{event.location}</InfoRow>}
                                </>
                            )
                        }
                    </AppPermissionsSwitcher>
                    {!event?.property?.length && !event?.project?.length && event?.location && (
                        <InfoRow icon="mdi-map-marker-outline">{event.location}</InfoRow>
                    )}
                    {event?.notes && (
                        <InfoRow icon="mdi-message-text-outline" secondary>
                            {event.notes}
                        </InfoRow>
                    )}
                    {event?.activityAgent && event.activityAgent.length && (
                        <InfoRow icon="mdi-account-outline">
                            {event.activityAgent.map((item, index, array) => (
                                <React.Fragment key={item.agent.id}>
                                    {item.agent.firstName} {item.agent.lastName}
                                    {index < array.length - 1 && ', '}
                                </React.Fragment>
                            ))}
                        </InfoRow>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <AppPermissionsSwitcher permission="ModulePermissions" activityAgentId={activityAgentId} module={'activities'} action={action.delete}>
                    {({ hasModulePermission }: PermissionsProps) =>
                        hasModulePermission && <DeleteButton id="btn-delete-event" onClick={handleDelete} />
                    }
                </AppPermissionsSwitcher>
            </ModalFooter>
        </>
    )
}

export default InfoContent
